import { css } from '@emotion/react';
import { ReactNode } from 'react';

import { Colors, Typography } from '@zapier/design-system';
import { mediaQueries } from '@zapier/identity-utils';
import { atoms, colors } from '@zapier/marketing-ui';

type Props = {
  children: ReactNode;
  footer?: ReactNode;
  promo?: ReactNode;
  showPromo?: boolean;
  title?: ReactNode;
  titlePosition?: 'center' | 'right';
  header?: ReactNode;
  align?: 'center' | 'start';
};

const styles = {
  root: css`
    background-color: ${Colors.PrimeWhite};
    display: flex;
    flex: 1 0 auto;
    flex-direction: column;
    min-height: 100%;
  `,

  inner: css`
    box-sizing: border-box;
    padding: 20px;
    position: relative;
    margin: 0 auto;

    ${mediaQueries.tablet} {
      padding: 64px 0;
      width: 100%;
    }

    ${mediaQueries.desktop} {
      max-width: 1120px;
      padding: 96px 0;
    }
  `,

  innerNonSplit: css`
    ${mediaQueries.tablet} {
      max-width: 500px;
    }
  `,

  title: css`
    ${Typography.Body4}
    margin-bottom: 30px;
    text-align: center;
    text-wrap: balance;
    ${mediaQueries.tablet} {
      ${Typography.SectionHeader}
    }
  `,

  contentWithPromo: css`
    display: grid;
    ${mediaQueries.tablet} {
      gap: 50px;
      grid-template-columns: minmax(0px, 1fr) 360px;
    }
    ${mediaQueries.desktop} {
      grid-template-columns: minmax(0px, 1fr) 430px;
    }
  `,

  alignCenter: css`
    align-items: center;
  `,

  alignStart: css`
    align-items: flex-start;
  `,

  formWrapper: css`
    display: grid;
    gap: 24px;
  `,

  promoOffset: css`
    padding-top: 54px;
  `,

  form: css`
    ${mediaQueries.desktop} {
      border-radius: 4px;
      border: 1px solid ${colors.neutral300};
      padding: 32px;
    }
  `,

  footer: css`
    margin-top: 20px;

    ${mediaQueries.tablet} {
      margin-top: 40px;
    }
  `,
};

export function AuthLayout({
  children,
  footer,
  promo,
  showPromo = true,
  title,
  titlePosition = 'center',
  header,
  align = 'center',
}: Props) {
  const shouldShowPromo = Boolean(showPromo && promo);

  return (
    <div css={styles.root}>
      <div css={[styles.inner, !shouldShowPromo && styles.innerNonSplit]}>
        {header}
        {title && titlePosition === 'center' && (
          <h1 css={styles.title}>{title}</h1>
        )}
        <div
          css={[
            shouldShowPromo && styles.contentWithPromo,
            align === 'center' ? styles.alignCenter : styles.alignStart,
          ]}
        >
          {shouldShowPromo && (
            <div
              data-testid="auth-promo"
              css={
                align === 'start' &&
                titlePosition === 'right' &&
                styles.promoOffset
              }
            >
              {promo}
            </div>
          )}
          <div css={styles.formWrapper}>
            {title && titlePosition === 'right' && (
              <h1
                className={`${atoms.headingXS} ${atoms.textNeutral900} ${atoms.textCenter}`}
              >
                {title}
              </h1>
            )}
            <div css={styles.form}>{children}</div>
          </div>
        </div>
        {footer && <footer css={styles.footer}>{footer}</footer>}
      </div>
    </div>
  );
}
